import { useCallback, useEffect, useRef, useState } from 'react';
import { getDepositSettings } from '../../api.deposit';
import { getStayExtensionSettings } from '../../api.stayExtension';
import { getApartmentById } from '../../api.apartments';

/**
 * Server facts the setup checklist needs that the apartment form itself does not carry.
 * `undefined` = not answered yet, `null` = the request failed (treated as "unknown": a failed read
 * must not block the host, see `stripeAccountStatus`).
 *
 * <p>Deposit and stay-extension settings autosave in their own components and the calendar is edited
 * through immediate actions, so none of them goes through this page's save. They are re-read when
 * the host leaves the group that edits them, and when the window regains focus.
 */
export interface ChecklistFacts {
  deposit: { enabled: boolean; amount?: number | null; currency?: string } | null | undefined;
  extensions: { mode: string; luggageMode: string } | null | undefined;
  /** `undefined` until re-read; the page then falls back to the value from its own load. */
  calendarSyncOk: boolean | null | undefined;
  refresh: (what: { money?: boolean; calendar?: boolean }) => void;
  refreshAll: () => void;
}

export function useChecklistFacts(apartmentId: number, token: () => string): ChecklistFacts {
  const [deposit, setDeposit] = useState<ChecklistFacts['deposit']>(undefined);
  const [extensions, setExtensions] = useState<ChecklistFacts['extensions']>(undefined);
  const [calendarSyncOk, setCalendarSyncOk] = useState<ChecklistFacts['calendarSyncOk']>(undefined);
  const alive = useRef(true);
  const tokenRef = useRef(token);
  tokenRef.current = token;

  useEffect(() => {
    alive.current = true;
    return () => { alive.current = false; };
  }, []);

  const refresh = useCallback((what: { money?: boolean; calendar?: boolean }) => {
    if (!apartmentId) return;
    if (what.money) {
      getDepositSettings(apartmentId)
        .then(s => { if (alive.current) setDeposit({ enabled: !!s.enabled, amount: s.amount ?? null, currency: s.currency }); })
        .catch(() => { if (alive.current) setDeposit(null); });
      getStayExtensionSettings(apartmentId)
        .then(s => { if (alive.current) setExtensions({ mode: s.mode, luggageMode: s.luggageMode }); })
        .catch(() => { if (alive.current) setExtensions(null); });
    }
    if (what.calendar) {
      getApartmentById(apartmentId, tokenRef.current())
        .then(a => { if (alive.current) setCalendarSyncOk(a?.calendarSyncOk ?? null); })
        .catch(() => { /* keep what the page already knows */ });
    }
  }, [apartmentId]);

  const refreshAll = useCallback(() => refresh({ money: true, calendar: true }), [refresh]);

  // First read: only the money facts — the apartment (with calendarSyncOk) is loaded by the page.
  useEffect(() => {
    refresh({ money: true });
  }, [refresh]);

  useEffect(() => {
    const onFocus = () => refreshAll();
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [refreshAll]);

  return { deposit, extensions, calendarSyncOk, refresh, refreshAll };
}
